import dayjs from 'dayjs';
import { v4 as uuid } from 'uuid';

import type { TObject } from '@altirllc/shared/utils/types/common';

import { isResponseError } from '@/utils/errorUtils/isResponseError';

import { trackLoki } from './trackLoki';
import { trackVerbose } from './trackVerbose';
import type { TLogCategory, TLogLevel, TLogRecord } from './types';

export class Logger {
  private userId: string | null = null;

  identifyUser(id: string) {
    this.userId = id;
  }

  resetUser() {
    this.userId = null;
  }

  warn(event: unknown, category: TLogCategory) {
    this.track('WARN', event, category);
  }

  error(event: unknown, category: TLogCategory) {
    this.track('ERROR', event, category);
  }

  private track(level: TLogLevel, event: unknown, category: TLogCategory) {
    const record: TLogRecord = {
      level,
      category,
      timeStamp: Date.now(),
      timeOpen: dayjs.duration(performance.now()).format('HH:mm:ss'),
      location: window.location.toString(),
      uiCommitHash: window.hash,
      userId: this.userId,

      ...Logger.eventToDetails(event),
    };

    record.correlationId = record.correlationId ?? uuid();

    trackVerbose(record);
    trackLoki(record);
  }

  static eventToDetails(error: unknown) {
    if (isResponseError<TObject>(error)) {
      return {
        code: error.code,
        message: error.message,
        status: error.response.status,
        requestId: error.response.headers?.['x-amzn-requestid'],
        request: `${error.config.method} ${error.config.baseURL}${error.config.url}`,
        correlationId: error.config.headers?.correlation_id as string | undefined,
        ...error.response.data,
      };
    }

    if (error instanceof Error) {
      return {
        message: error.message,
        stack: error.stack,
      };
    }

    if (typeof error === 'string') {
      return { message: error };
    }

    return { message: 'Unknown error' };
  }
}
